import Button from '../../atoms/button'
import Card from '../../atoms/card'
import CookieIcon from '../../atoms/icons/cookie-icon'
import Link from '../../atoms/link'
import Paragraph from '../../atoms/paragraph'

export interface CookieConsentBannerProps {
  /**
   * Title displayed on the banner.
   */
  title: string
  /**
   * Description displayed on the banner.
   */
  description: string
  /**
   * Link to privacy policy or cookie policy page.
   *
   * @default null
   */
  href?: string
  /**
   * Text displayed on anchor element if 'href' is provided.
   *
   * @default 'Learn more'
   */
  linkText?: string
  /**
   * Text displayed on "Change Preferences" button.
   *
   * @default 'Change Preferences'
   */
  changePreferenceText?: string
  /**
   * Text displayed on "Decline" button.
   *
   * @default 'Decline'
   */
  declineText?: string
  /**
   * Text displayed on "Accept" button.
   *
   * @default 'Accept'
   */
  acceptText?: string
  /**
   * Function called when "Change Preferences" button is pressed.
   */
  onChangePreferences?: VoidFunction
  /**
   * Function called when "Decline" button is pressed.
   */
  onDecline?: VoidFunction
  /**
   * Function called when "Accept" button is pressed.
   */
  onAccept?: VoidFunction
}

export function CookieConsentBanner({
  title,
  description,
  href,
  linkText = 'Learn more',
  changePreferenceText = 'Change Preferences',
  declineText = 'Decline',
  acceptText = 'Accept',
  onChangePreferences,
  onDecline,
  onAccept,
}: CookieConsentBannerProps) {
  return (
    <Card
      color="dark"
      padding="none"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
      aria-modal="false"
      className="grid fixed bottom-4 md:bottom-6 py-4 px-6 left-0 right-0 mx-auto w-11/12 lg:10/12 xl:w-9/12 text-white grid-flow-row lg:grid-flow-col gap-4 justify-center items-center lg:justify-between z-50"
      borderRadius="rounded-3xl"
    >
      <div className="grid grid-flow-col justify-items-start gap-2">
        <CookieIcon />

        <div className="grid grid-flow-row justify-items-start">
          <strong id="cookie-banner-title" className="text-lg sm:text-xl">
            {title}
          </strong>

          <Paragraph
            id="cookie-banner-description"
            className="text-sm sm:text-base"
            color="light"
          >
            {description}
          </Paragraph>

          {href && (
            <Link
              href={href}
              variant="secondary"
              active
              shouldDisplayActiveUnderline={false}
              fontWeight="normal"
              className="hover:underline text-sm sm:text-base"
            >
              {linkText}
            </Link>
          )}
        </div>
      </div>

      <div className="grid grid-flow-row w-5/6 justify-self-center lg:justify-self-end lg:w-full sm:grid-flow-col gap-2 items-center scale-90 lg:origin-right">
        <Button
          variant="secondary"
          onClick={onChangePreferences}
          environment="dark"
        >
          {changePreferenceText}
        </Button>

        <Button variant="primary" onClick={onDecline} environment="dark">
          {declineText}
        </Button>

        <Button variant="primary" onClick={onAccept} environment="dark">
          {acceptText}
        </Button>
      </div>
    </Card>
  )
}

export default CookieConsentBanner
